import React, { useState, useEffect, useCallback } from 'react'
import {
  Row, Col, Button, Spinner, Input,
} from 'reactstrap'
import ClausulaModal from './ClausulaModal'
import {
  getSecoes, getClausulas, getBlocos, getContratoCampos,
  saveSecao, delSecao, delClausula, delBloco,
  handlerExiste, normalizaHandler,
} from './minutaApi'
import { plainTextFromBody } from './minutaRender'
import { notifyError, notifySuccess } from '../../utils/notify'

const BLOCO_TIPO_LABEL = {
  preambulo: 'Preâmbulo',
  qualificacao: 'Qualificação',
  considerandos: 'Considerandos',
  fecho: 'Fecho',
  assinaturas: 'Assinaturas',
  testemunhas: 'Testemunhas',
  anexo: 'Anexo',
  outro: 'Outro',
}

const SEM_SECAO = '__sem_secao__'

const CatalogoTab = () => {
  const [loading, setLoading] = useState(true)
  const [secoes, setSecoes] = useState([])
  const [clausulas, setClausulas] = useState([])
  const [blocos, setBlocos] = useState([])
  const [campos, setCampos] = useState([])

  // edição inline de seção: { id, nome, hand } | null  (id='new' para nova)
  const [editSec, setEditSec] = useState(null)
  // modal de cláusula/bloco
  const [modal, setModal] = useState({ open: false, kind: 'clausula', record: null })

  const carregar = useCallback(async () => {
    setLoading(true)
    const [s, c, b] = await Promise.all([getSecoes(), getClausulas(), getBlocos()])
    setSecoes(s || [])
    setClausulas(c || [])
    setBlocos(b || [])
    setLoading(false)
  }, [])

  useEffect(() => {
    carregar()
    getContratoCampos().then(setCampos)
  }, [carregar])

  // ----- Seções (inline) -----
  const iniciarNovaSecao = () => setEditSec({ id: 'new', nome: '', hand: '' })

  const salvarSecao = async () => {
    if (!editSec) return
    const nome = editSec.nome.trim()
    const hand = normalizaHandler(editSec.hand || editSec.nome)
    if (!nome) { notifyError('Informe o nome da seção.'); return }
    if (!hand) { notifyError('Informe um handler válido.'); return }

    const id = editSec.id === 'new' ? null : editSec.id
    const dup = await handlerExiste('minutaSec', hand, id)
    if (dup) { notifyError(`O handler "${hand}" já está em uso.`); return }

    const data = { minutaSec_nome: nome, minutaSec_hand: hand }
    if (id) data.minutaSec_id = id
    await saveSecao(data)
    setEditSec(null)
    notifySuccess('Seção salva.')
    carregar()
  }

  const excluirSecao = async (sec) => {
    const filhas = clausulas.filter((c) => String(c.minutaSec_id) === String(sec.minutaSec_id))
    const msg = filhas.length
      ? `Excluir a seção "${sec.minutaSec_nome}"? As ${filhas.length} cláusula(s) vinculada(s) ficarão sem seção.`
      : `Excluir a seção "${sec.minutaSec_nome}"?`
    // eslint-disable-next-line no-alert
    if (!window.confirm(msg)) return
    await delSecao(sec.minutaSec_id)
    notifySuccess('Seção excluída.')
    carregar()
  }

  // ----- Cláusulas / Blocos (modal) -----
  const abrirModal = (kind, record = null) => setModal({ open: true, kind, record })
  const fecharModal = () => setModal({ open: false, kind: 'clausula', record: null })
  const aoSalvarModal = () => { fecharModal(); carregar() }

  const excluirClausula = async (cl) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Excluir a cláusula "${cl.minutaClaus_nome}"?`)) return
    await delClausula(cl.minutaClaus_id)
    notifySuccess('Cláusula excluída.')
    carregar()
  }

  const excluirBloco = async (bl) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Excluir o bloco "${bl.minutaBloco_nome}"?`)) return
    await delBloco(bl.minutaBloco_id)
    notifySuccess('Bloco excluído.')
    carregar()
  }

  // agrupa cláusulas por seção
  const clausulasPorSecao = (secId) => clausulas.filter(
    (c) => String(c.minutaSec_id || SEM_SECAO) === String(secId),
  )
  const clausulasSemSecao = clausulas.filter((c) => !c.minutaSec_id)

  if (loading) {
    return <div className="text-center py-5"><Spinner /></div>
  }

  const renderSecaoHeader = (sec) => {
    const emEdicao = editSec && editSec.id === sec.minutaSec_id
    if (emEdicao) {
      return (
        <div className="minuta-sec-header">
          <Input
            className="minuta-inline-input"
            autoFocus
            value={editSec.nome}
            placeholder="Nome da seção"
            onChange={(e) => setEditSec({ ...editSec, nome: e.target.value })}
            onKeyDown={(e) => { if (e.key === 'Enter') salvarSecao(); if (e.key === 'Escape') setEditSec(null) }}
          />
          <Input
            style={{ maxWidth: 200 }}
            value={editSec.hand}
            placeholder="handler"
            onChange={(e) => setEditSec({ ...editSec, hand: e.target.value })}
            onKeyDown={(e) => { if (e.key === 'Enter') salvarSecao(); if (e.key === 'Escape') setEditSec(null) }}
          />
          <Button size="sm" color="primary" onClick={salvarSecao}>Salvar</Button>
          <Button size="sm" color="light" onClick={() => setEditSec(null)}>Cancelar</Button>
        </div>
      )
    }
    return (
      <div className="minuta-sec-header">
        <span className="minuta-sec-nome">{sec.minutaSec_nome}</span>
        <span className="minuta-sec-hand">{sec.minutaSec_hand}</span>
        <Button
          size="sm"
          color="link"
          title="Editar seção"
          onClick={() => setEditSec({ id: sec.minutaSec_id, nome: sec.minutaSec_nome, hand: sec.minutaSec_hand })}
        >
          <i className="mdi mdi-pencil" />
        </Button>
        <Button size="sm" color="link" className="text-danger" title="Excluir seção" onClick={() => excluirSecao(sec)}>
          <i className="mdi mdi-delete" />
        </Button>
      </div>
    )
  }

  const renderClausulaRow = (cl) => (
    <div className="minuta-claus-row" key={cl.minutaClaus_id}>
      <span className="minuta-claus-nome">
        {cl.minutaClaus_nome}
        <small className="text-muted ml-2" style={{ fontWeight: 400 }}>
          {plainTextFromBody(cl.minutaClaus_body).slice(0, 80)}
        </small>
      </span>
      <span className="minuta-sec-hand">{cl.minutaClaus_hand}</span>
      <Button size="sm" color="link" title="Editar" onClick={() => abrirModal('clausula', cl)}>
        <i className="mdi mdi-pencil" />
      </Button>
      <Button size="sm" color="link" className="text-danger" title="Excluir" onClick={() => excluirClausula(cl)}>
        <i className="mdi mdi-delete" />
      </Button>
    </div>
  )

  return (
    <>
      <Row>
        {/* Coluna principal: árvore seção › cláusula */}
        <Col lg={8}>
          <div className="d-flex justify-content-between align-items-center mb-2">
            <h5 className="mb-0">Seções e Cláusulas</h5>
            <div>
              <Button color="light" size="sm" className="mr-2" onClick={iniciarNovaSecao}>
                <i className="mdi mdi-plus mr-1" /> Nova Seção
              </Button>
              <Button color="primary" size="sm" onClick={() => abrirModal('clausula')}>
                <i className="mdi mdi-plus mr-1" /> Nova Cláusula
              </Button>
            </div>
          </div>

          <div className="minuta-tree">
            {editSec && editSec.id === 'new' && (
              <div className="minuta-tree-sec">
                <div className="minuta-sec-header">
                  <Input
                    className="minuta-inline-input"
                    autoFocus
                    value={editSec.nome}
                    placeholder="Nome da nova seção"
                    onChange={(e) => setEditSec({ ...editSec, nome: e.target.value })}
                    onKeyDown={(e) => { if (e.key === 'Enter') salvarSecao(); if (e.key === 'Escape') setEditSec(null) }}
                  />
                  <Input
                    style={{ maxWidth: 200 }}
                    value={editSec.hand}
                    placeholder="handler (auto)"
                    onChange={(e) => setEditSec({ ...editSec, hand: e.target.value })}
                    onKeyDown={(e) => { if (e.key === 'Enter') salvarSecao(); if (e.key === 'Escape') setEditSec(null) }}
                  />
                  <Button size="sm" color="primary" onClick={salvarSecao}>Salvar</Button>
                  <Button size="sm" color="light" onClick={() => setEditSec(null)}>Cancelar</Button>
                </div>
              </div>
            )}

            {secoes.map((sec) => (
              <div className="minuta-tree-sec" key={sec.minutaSec_id}>
                {renderSecaoHeader(sec)}
                {clausulasPorSecao(sec.minutaSec_id).map(renderClausulaRow)}
                {clausulasPorSecao(sec.minutaSec_id).length === 0 && (
                  <div className="minuta-claus-row text-muted" style={{ fontStyle: 'italic' }}>
                    Sem cláusulas nesta seção.
                  </div>
                )}
              </div>
            ))}

            {/* Cláusulas sem seção */}
            {clausulasSemSecao.length > 0 && (
              <div className="minuta-tree-sec">
                <div className="minuta-sec-header">
                  <span className="minuta-sec-nome text-muted">Sem seção</span>
                </div>
                {clausulasSemSecao.map(renderClausulaRow)}
              </div>
            )}

            {secoes.length === 0 && clausulas.length === 0 && (
              <div className="p-4 text-center text-muted">
                Nenhuma seção ou cláusula cadastrada.
              </div>
            )}
          </div>
        </Col>

        {/* Coluna lateral: blocos estruturais */}
        <Col lg={4}>
          <div className="d-flex justify-content-between align-items-center mb-2">
            <h5 className="mb-0">Blocos estruturais</h5>
            <Button color="primary" size="sm" onClick={() => abrirModal('bloco')}>
              <i className="mdi mdi-plus mr-1" /> Novo Bloco
            </Button>
          </div>
          <div className="minuta-tree">
            {blocos.map((bl) => (
              <div className="minuta-claus-row" key={bl.minutaBloco_id} style={{ paddingLeft: 14 }}>
                <span className="minuta-claus-nome">
                  {bl.minutaBloco_nome}
                  <span className="minuta-item-tipo ml-2">{BLOCO_TIPO_LABEL[bl.minutaBloco_tipo] || bl.minutaBloco_tipo}</span>
                </span>
                <span className="minuta-sec-hand">{bl.minutaBloco_hand}</span>
                <Button size="sm" color="link" title="Editar" onClick={() => abrirModal('bloco', bl)}>
                  <i className="mdi mdi-pencil" />
                </Button>
                <Button size="sm" color="link" className="text-danger" title="Excluir" onClick={() => excluirBloco(bl)}>
                  <i className="mdi mdi-delete" />
                </Button>
              </div>
            ))}
            {blocos.length === 0 && (
              <div className="p-4 text-center text-muted">Nenhum bloco cadastrado.</div>
            )}
          </div>
        </Col>
      </Row>

      <ClausulaModal
        isOpen={modal.open}
        kind={modal.kind}
        record={modal.record}
        secoes={secoes}
        campos={campos}
        onClose={fecharModal}
        onSaved={aoSalvarModal}
      />
    </>
  )
}

export default CatalogoTab
